// React
import { useState, useEffect } from 'react'

// Mantine
import { Input, InputBase, Combobox, useCombobox, CheckIcon } from '@mantine/core'
import { useDidUpdate, useDebouncedValue } from '@mantine/hooks'

// Search
import useSearch from '@/hooks/useSearch'

export default function ComponentMultiSelect({
    label = 'Sélectionner des composants',
    placeholder = 'Sélectionner des composants',
    value = [],
    onChange = () => null,
}) {
    const { singleSearch, isReady } = useSearch()

    const combobox = useCombobox()

    const [selected, setSelected] = useState(value ?? [])
    const [query, setQuery] = useState('')
    const [debouncedQuery] = useDebouncedValue(query, 300)
    const [results, setResults] = useState([])

    // Libellés des composants sélectionnés, pour pouvoir les afficher et les
    // retirer sans avoir à les rechercher à nouveau.
    const [selectedDocs, setSelectedDocs] = useState({})

    // Pas de recherche à vide : l'endpoint exige une requête, et on ne veut pas
    // dérouler la liste complète des composants à l'ouverture.
    useEffect(() => {
        if (!isReady) return

        if (!debouncedQuery.trim()) {
            setResults([])
            return
        }

        let cancelled = false

        singleSearch('components', debouncedQuery, { hitsPerPage: 50 })
            .then(({ hits }) => {
                if (!cancelled) setResults(hits)
            })
            .catch(() => {
                if (!cancelled) setResults([])
            })

        return () => {
            cancelled = true
        }
    }, [debouncedQuery, isReady])

    const isSelected = (id) => selected.some((item) => String(item) === String(id))

    const onOptionSubmit = (component) => {
        const id = String(component.id)

        if (isSelected(id)) {
            setSelected(selected.filter((item) => String(item) !== id))
        } else {
            setSelected([...selected, id])
            setSelectedDocs((docs) => ({ ...docs, [id]: component }))
        }
    }

    useDidUpdate(() => {
        onChange(selected)
    }, [selected])

    // Sélectionnés en haut, puis les résultats de recherche non déjà sélectionnés
    const selectedOptions = selected.map(
        (id) => selectedDocs[String(id)] ?? { id, name: `Composant #${id}` }
    )
    const searchOptions = results.filter((hit) => !isSelected(hit.id))

    const renderOption = (component) => (
        <Combobox.Option
            key={component.id}
            value={component}
            className="flex w-full items-start gap-2"
        >
            <span className="flex h-6 w-3 shrink-0 items-center">
                {isSelected(component.id) && <CheckIcon size={10} />}
            </span>

            <span className="min-w-0 flex-1">
                <span className="block truncate leading-6" title={component.name}>{component.name}</span>
                {component.reference && (
                    <span className="mt-0.5 block text-xs leading-4 text-gray-500">
                        {component.reference}
                    </span>
                )}
            </span>
        </Combobox.Option>
    )

    return (
        <Combobox
            resetSelectionOnOptionHover
            store={combobox}
            onOptionSubmit={onOptionSubmit}
            withinPortal={false}
        >
            <Combobox.Target>
                <InputBase
                    label={label}
                    component="button"
                    type="button"
                    pointer
                    rightSection={<Combobox.Chevron />}
                    rightSectionPointerEvents="none"
                    onClick={() => combobox.toggleDropdown()}
                >
                    {selected.length === 0 && (
                        <Input.Placeholder>{placeholder}</Input.Placeholder>
                    )}

                    {selected.length > 0 && (
                        <span>{selected.length} composant(s) sélectionné(s)</span>
                    )}
                </InputBase>
            </Combobox.Target>

            <Combobox.Dropdown>
                <Combobox.Search
                    placeholder="Chercher un composant (nom ou SKU)"
                    value={query}
                    onChange={(event) => setQuery(event.currentTarget.value)}
                />

                <Combobox.Options mah={400} style={{ overflowY: 'auto' }}>
                    {selectedOptions.length > 0 && (
                        <Combobox.Group label="Sélectionnés">
                            {selectedOptions.map(renderOption)}
                        </Combobox.Group>
                    )}

                    {searchOptions.length > 0 && (
                        <Combobox.Group label="Résultats">
                            {searchOptions.map(renderOption)}
                        </Combobox.Group>
                    )}

                    {searchOptions.length === 0 && (
                        <Combobox.Empty>
                            {debouncedQuery.trim()
                                ? 'Aucun composant'
                                : 'Tapez pour rechercher un composant'}
                        </Combobox.Empty>
                    )}
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    )
}
